import { Form, required, SaveButton, SelectInput, useRecordContext } from "react-admin";
import { useEffect, useState } from "react";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { drivingSchoolsProvider } from "../providers/drivingSchoolsProvider";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { getAuthenticatedBackendClient } from "../api/backend.api";
import { PaymentStrategyEnum } from "../generated/backendClient";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { useQueryClient } from "react-query";

export type FrontendPaymentStrategyEnum =
  | "upfrontPayment"
  | "payAsYouDrive"
  | "purchaseOnAccount"
  | "externalBankTransfer";

export const PaymentStrategyChoices: { id: FrontendPaymentStrategyEnum; name: string }[] = [
  { id: "upfrontPayment", name: "nur Vorauszahlung" },
  { id: "payAsYouDrive", name: "Zahlung nach jedem Termin" },
  { id: "purchaseOnAccount", name: "per Rechnung" },
  { id: "externalBankTransfer", name: "per externer Banküberweisung" },
];

export const frontendToBackend: { [k in FrontendPaymentStrategyEnum]: PaymentStrategyEnum } = {
  upfrontPayment: "upfront_payment",
  payAsYouDrive: "pay_as_you_drive",
  purchaseOnAccount: "purchase_on_account",
  externalBankTransfer: "external_bank_transfer",
};

const filterDrivingSchoolPaymentStrategyChoices = (
  choices: typeof PaymentStrategyChoices,
  drivingSchool: DrivingSchool | undefined,
) => {
  if (drivingSchool?.customizations?.includes("canUseExternalBankTransfer")) {
    return choices;
  }
  return choices.filter((choice) => choice.id !== "externalBankTransfer");
};

export function ChangeStudentPaymentStrategyDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const student = useRecordContext<Student>();
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const [drivingSchool, setDrivingSchool] = useState<DrivingSchool | undefined>(undefined);
  useEffect(() => {
    setDrivingSchool(drivingSchoolsProvider.getOneFromCache(student.drivingSchoolId));
  }, [student.drivingSchoolId]);

  const onSubmit = async ({ paymentStrategy }: Partial<Student>) => {
    if (!student) {
      throw new Error("Invalid state: !student");
    }
    if (!paymentStrategy) {
      throw new Error("No paymentStrategy selected");
    }
    setSaving(true);
    try {
      const updateReceived = new Promise<void>((resolve) => {
        const unsubscribe = studentsProvider.onUpdate(student, (student) => {
          if (student.paymentStrategy === paymentStrategy) {
            unsubscribe();
            resolve();
          }
        });
      });
      const backendValue = frontendToBackend[paymentStrategy];
      const backendClient = await getAuthenticatedBackendClient();
      await backendClient.user.userSetPaymentStrategyCreate({
        id: student.id,
        requestBody: { payment_strategy: backendValue },
      });
      await updateReceived;
      await queryClient.invalidateQueries(["students", "getOne", { id: student.id }]);
      showSuccessToast(`Zahlungsmodus von ${student.name} erfolgreich geändert.`);
    } catch (e) {
      console.error(e);
      showErrorToast(`Fehler beim Ändern des Zahlungsmodus von ${student.name}`);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Zahlungsmodus von {student.name} ändern</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Form warnWhenUnsavedChanges onSubmit={onSubmit}>
        <DialogContent sx={{ width: "calc(100% - 100px)" }}>
          <SelectInput
            sx={{ width: "20em" }}
            label="Zahlungsart"
            source="paymentStrategy"
            choices={filterDrivingSchoolPaymentStrategyChoices(PaymentStrategyChoices, drivingSchool)}
            disabled={saving}
            validate={required()}
          />
          <Alert severity="warning">Der geänderte Zahlungsmodus wirkt sich nur auf zukünftige Rechnungen aus.</Alert>
          <Typography sx={{ mt: 1, mb: 1 }} variant="body2">
            Für bereits gestellte Rechnungen muss eine Zahlungswiederholung gestartet werden, bei dem man die dafür
            gewünschte Zahlungsart auswählt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SaveButton icon={<></>} />
        </DialogActions>
      </Form>
    </Dialog>
  );
}
